import type { Site } from '@hotjar/site';
import type { SelectedPlans } from '@hotjar/subscription-management';
import { SitePlanInfo } from '@hotjar/webapp/billing/plan';
import {
  knownReferrers,
  multiProductsReferrers,
  planComparisonReferrers,
  trafficRecommendationReferrers,
} from '@hotjar/webapp/features/Billing/PlanComparisonModal/constants';

import type {
  KnownReferrer,
  PlanComparisonReferrer,
  TrafficRecommendationReferrer,
} from '@hotjar/webapp/features/Billing/PlanComparisonModal/types';
import { getBundledPlanId } from '@hotjar/webapp/domains/common/product/utils';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';

export function isMultiProductsReferrer(
  maybeIsReferrer: unknown,
): maybeIsReferrer is PlanComparisonReferrer {
  return multiProductsReferrers.some((referrer) => referrer === maybeIsReferrer);
}

export function isPlanComparisonReferrer(
  maybeIsReferrer: unknown,
): maybeIsReferrer is PlanComparisonReferrer {
  return planComparisonReferrers.some((referrer) => referrer === maybeIsReferrer);
}

export function isTrafficRecommendationReferrer(
  maybeIsReferrer: unknown,
): maybeIsReferrer is TrafficRecommendationReferrer {
  return trafficRecommendationReferrers.some((referrer) => referrer === maybeIsReferrer);
}

export function isKnownReferrer(
  maybeIsKnownReferrer: unknown,
): maybeIsKnownReferrer is KnownReferrer {
  return knownReferrers.some((referrer) => referrer === maybeIsKnownReferrer);
}

export function mapSelectedPlansToSitePlanInfos(
  selectedPlans: SelectedPlans,
  sites: Array<Site>,
): Array<SitePlanInfo> {
  return Object.keys(selectedPlans).map((siteIdString) => {
    const siteId = parseInt(siteIdString, 10);
    const products = selectedPlans[siteId];
    const site = sites.find((siteObject) => siteObject.id === siteId);

    if (!site) {
      throw new Error(
        `mapSelectedPlansToSitePlanInfos(): Could not retrieve site from sites array by ID ${siteId}.`,
      );
    }

    if (!products[ProductType.Analytics] || !products[ProductType.Feedback]) {
      throw new Error(
        'mapSelectedPlansToSitePlanInfos(): Value of selectedPlans object was fully or partially empty.',
      );
    }

    // Plan selections are coming in in the `Product` shape, we need to convert them to a bundled plan ID.
    const bundledPlanId = getBundledPlanId({
      analyticsPlanId: products[ProductType.Analytics].sku,
      feedbackPlanId: products[ProductType.Feedback].sku,
    });

    return new SitePlanInfo(siteId, bundledPlanId);
  });
}
